import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  ArrowLeft, Plus, Trash2, GripVertical, Mail, Bell, ClipboardList,
  ChevronDown, ChevronUp, Save, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const CAMPAIGN_TYPES = ['Listing Agent', 'Buyer', 'General', 'Custom'];
const ACTION_TYPES = [
  { value: 'send_email', label: 'Send Email', icon: Mail, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { value: 'create_task', label: 'Create Task', icon: ClipboardList, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { value: 'notification', label: 'Notification', icon: Bell, color: 'text-purple-600 bg-purple-50 border-purple-200' },
];
const DELAY_TYPES = [
  { value: 'immediate', label: 'Immediately' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];
const TASK_TYPES = ['call', 'text', 'other'];
const LEAD_STATUSES = ['Under Contract', 'Marketed', 'Sold', 'Closed', 'Dead', 'Cancelled'];

const inputClass = "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-[#0073ea]/50 focus:ring-[#0073ea]";

const emptyStep = {
  action_type: 'send_email',
  email_template_id: '',
  email_subject: '',
  email_body: '',
  task_title: '',
  task_type: 'call',
  notification_message: '',
  delay_type: 'immediate',
  delay_value: 0,
};

export default function CampaignBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [name, setName] = useState('');
  const [campaignType, setCampaignType] = useState('General');
  const [steps, setSteps] = useState([]);
  const [stopOnStatuses, setStopOnStatuses] = useState([]);
  const [stopOnResponse, setStopOnResponse] = useState(false);
  const [businessHoursOnly, setBusinessHoursOnly] = useState(true);
  const [status, setStatus] = useState('draft');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [expandedStep, setExpandedStep] = useState(null);

  const fetchCampaign = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await API.get(`/drip-campaigns/${id}`);
      const c = res.data;
      setName(c.name);
      setCampaignType(c.campaign_type);
      setSteps(c.steps || []);
      setStopOnStatuses(c.stop_on_statuses || []);
      setStopOnResponse(c.stop_on_response || false);
      setBusinessHoursOnly(c.business_hours_only !== false);
      setStatus(c.status);
    } catch {
      toast.error('Campaign not found');
      navigate('/drip-campaigns');
    } finally { setLoading(false); }
  }, [id, isNew, navigate]);

  const fetchEmailTemplates = useCallback(async () => {
    try {
      const res = await API.get('/email-templates');
      setEmailTemplates(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchCampaign(); fetchEmailTemplates(); }, [fetchCampaign, fetchEmailTemplates]);

  const addStep = () => {
    const newStep = { ...emptyStep, id: `temp-${Date.now()}` };
    setSteps(prev => [...prev, newStep]);
    setExpandedStep(steps.length);
  };

  const removeStep = (index) => {
    setSteps(prev => prev.filter((_, i) => i !== index));
    setExpandedStep(null);
  };

  const updateStep = (index, field, value) => {
    setSteps(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const moveStep = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= steps.length) return;
    setSteps(prev => {
      const arr = [...prev];
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr;
    });
    setExpandedStep(newIndex);
  };

  const toggleStatus = (statusVal) => {
    setStopOnStatuses(prev =>
      prev.includes(statusVal) ? prev.filter(s => s !== statusVal) : [...prev, statusVal]
    );
  };

  const handleSave = async (saveStatus) => {
    if (!name.trim()) { toast.error('Campaign name is required'); return; }
    if (steps.length === 0) { toast.error('Add at least one step'); return; }
    setSaving(true);
    const payload = {
      name: name.trim(),
      campaign_type: campaignType,
      steps: steps.map(({ id: _id, ...rest }) => rest),
      stop_on_statuses: stopOnStatuses,
      stop_on_response: stopOnResponse,
      business_hours_only: businessHoursOnly,
      status: saveStatus || status,
    };
    try {
      if (isNew) {
        const res = await API.post('/drip-campaigns', payload);
        toast.success('Campaign created');
        navigate(`/drip-campaigns/${res.data.id}/edit`);
      } else {
        await API.put(`/drip-campaigns/${id}`, payload);
        toast.success('Campaign saved');
        setStatus(saveStatus || status);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const getActionMeta = (type) => ACTION_TYPES.find(a => a.value === type) || ACTION_TYPES[0];

  const formatDelayLabel = (step) => {
    if (step.delay_type === 'immediate') return 'Immediately';
    return `${step.delay_value} ${step.delay_type}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-[#0073ea] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="campaign-builder-page" className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost" size="sm"
          data-testid="back-to-campaigns"
          onClick={() => navigate('/drip-campaigns')}
          className="text-gray-500 hover:text-gray-900 h-9 px-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">{isNew ? 'New Campaign' : 'Edit Campaign'}</h1>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && status !== 'active' && (
            <Button
              data-testid="activate-campaign-btn"
              onClick={() => handleSave('active')}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Zap className="h-4 w-4 mr-1" /> Activate
            </Button>
          )}
          {!isNew && status === 'active' && (
            <Button
              data-testid="pause-campaign-btn"
              onClick={() => handleSave('paused')}
              disabled={saving}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              Pause
            </Button>
          )}
          <Button
            data-testid="save-campaign-btn"
            onClick={() => handleSave(null)}
            disabled={saving}
            className="bg-[#0073ea] hover:bg-[#0060c2] text-white"
          >
            <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Campaign Details Card */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1 space-y-1.5">
              <Label className="text-gray-600 text-xs">Campaign Name *</Label>
              <Input
                data-testid="campaign-name-input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Offer Follow-Up Sequence"
                className={inputClass}
              />
            </div>
            <div className="col-span-2 sm:col-span-1 space-y-1.5">
              <Label className="text-gray-600 text-xs">Campaign Type</Label>
              <Select value={campaignType} onValueChange={setCampaignType}>
                <SelectTrigger data-testid="campaign-type-select" className={inputClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {CAMPAIGN_TYPES.map(t => (
                    <SelectItem key={t} value={t} className="text-gray-900">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator className="bg-gray-200" />

          {/* Conditions */}
          <div className="space-y-3">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Conditions & Settings</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-900 font-medium">Business Hours Only</p>
                <p className="text-xs text-gray-500">8:30 AM – 7:30 PM, Mon–Sat (EST)</p>
              </div>
              <Switch
                data-testid="business-hours-toggle"
                checked={businessHoursOnly}
                onCheckedChange={setBusinessHoursOnly}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-900 font-medium">Stop on Response</p>
                <p className="text-xs text-gray-500">Automatically stop if a reply is received</p>
              </div>
              <Switch
                data-testid="stop-on-response-toggle"
                checked={stopOnResponse}
                onCheckedChange={setStopOnResponse}
              />
            </div>
            <div>
              <p className="text-sm text-gray-900 font-medium mb-2">Stop if Lead Status Changes To</p>
              <div className="flex flex-wrap gap-2">
                {LEAD_STATUSES.map(s => (
                  <button
                    key={s}
                    data-testid={`stop-status-${s.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => toggleStatus(s)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      stopOnStatuses.includes(s)
                        ? 'bg-red-50 text-red-700 border-red-300'
                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Steps */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Steps ({steps.length})</h2>
          <Button
            data-testid="add-step-btn"
            onClick={addStep}
            variant="outline"
            size="sm"
            className="border-[#0073ea]/30 text-[#0073ea] hover:bg-[#0073ea]/5"
          >
            <Plus className="h-4 w-4 mr-1" /> Add Step
          </Button>
        </div>

        {steps.length === 0 && (
          <Card className="bg-gray-50 border-dashed border-gray-300">
            <CardContent className="p-8 text-center">
              <Zap className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No steps yet. Add your first step to start building the sequence.</p>
              <Button onClick={addStep} className="mt-3 bg-[#0073ea] hover:bg-[#0060c2] text-white" size="sm">
                <Plus className="h-4 w-4 mr-1" /> Add First Step
              </Button>
            </CardContent>
          </Card>
        )}

        {steps.map((step, index) => {
          const meta = getActionMeta(step.action_type);
          const Icon = meta.icon;
          const isExpanded = expandedStep === index;

          return (
            <Card
              key={step.id || index}
              data-testid={`step-card-${index}`}
              className={`bg-white border-gray-200 transition-all ${isExpanded ? 'ring-1 ring-[#0073ea]/30' : ''}`}
            >
              <CardContent className="p-0">
                {/* Step Header - always visible */}
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                  onClick={() => setExpandedStep(isExpanded ? null : index)}
                >
                  <div className="flex items-center gap-1 text-gray-400">
                    <GripVertical className="h-4 w-4" />
                    <span className="text-xs font-mono w-5 text-center">{index + 1}</span>
                  </div>
                  <div className={`flex items-center gap-1.5 px-2 py-1 rounded border text-xs font-medium ${meta.color}`}>
                    <Icon className="h-3.5 w-3.5" />
                    {meta.label}
                  </div>
                  <div className="flex-1 text-sm text-gray-600 truncate">
                    {step.action_type === 'send_email' && (step.email_subject || 'No subject set')}
                    {step.action_type === 'create_task' && (step.task_title || 'No task title')}
                    {step.action_type === 'notification' && (step.notification_message || 'No message')}
                  </div>
                  <Badge variant="outline" className="text-xs text-gray-500 border-gray-200">
                    {formatDelayLabel(step)}
                  </Badge>
                  {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>

                {/* Step Details - expanded */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100 space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-gray-600 text-xs">Action Type</Label>
                        <Select value={step.action_type} onValueChange={v => updateStep(index, 'action_type', v)}>
                          <SelectTrigger data-testid={`step-action-type-${index}`} className={inputClass}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-200">
                            {ACTION_TYPES.map(a => (
                              <SelectItem key={a.value} value={a.value} className="text-gray-900">{a.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-gray-600 text-xs">Delay</Label>
                        <Select value={step.delay_type} onValueChange={v => updateStep(index, 'delay_type', v)}>
                          <SelectTrigger data-testid={`step-delay-type-${index}`} className={inputClass}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-200">
                            {DELAY_TYPES.map(d => (
                              <SelectItem key={d.value} value={d.value} className="text-gray-900">{d.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {step.delay_type !== 'immediate' && (
                        <div className="space-y-1.5">
                          <Label className="text-gray-600 text-xs">
                            {step.delay_type === 'hours' ? 'Hours' : 'Days'}
                          </Label>
                          <Input
                            data-testid={`step-delay-value-${index}`}
                            type="number"
                            min="1"
                            value={step.delay_value || ''}
                            onChange={e => updateStep(index, 'delay_value', parseInt(e.target.value) || 0)}
                            placeholder="1"
                            className={inputClass}
                          />
                        </div>
                      )}
                    </div>

                    {/* Email fields */}
                    {step.action_type === 'send_email' && (
                      <div className="space-y-3">
                        {emailTemplates.length > 0 && (
                          <div className="space-y-1.5">
                            <Label className="text-gray-600 text-xs">Email Template (optional)</Label>
                            <Select
                              value={step.email_template_id || 'none'}
                              onValueChange={v => {
                                updateStep(index, 'email_template_id', v === 'none' ? '' : v);
                                if (v !== 'none') {
                                  const tpl = emailTemplates.find(t => t.id === v);
                                  if (tpl) {
                                    updateStep(index, 'email_subject', tpl.subject || '');
                                    updateStep(index, 'email_body', tpl.html_content || '');
                                  }
                                }
                              }}
                            >
                              <SelectTrigger data-testid={`step-email-template-${index}`} className={inputClass}>
                                <SelectValue placeholder="Select template..." />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200">
                                <SelectItem value="none" className="text-gray-900">No template</SelectItem>
                                {emailTemplates.map(t => (
                                  <SelectItem key={t.id} value={t.id} className="text-gray-900">{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="space-y-1.5">
                          <Label className="text-gray-600 text-xs">Subject</Label>
                          <Input
                            data-testid={`step-email-subject-${index}`}
                            value={step.email_subject}
                            onChange={e => updateStep(index, 'email_subject', e.target.value)}
                            placeholder="Follow up on {property_address}"
                            className={inputClass}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-gray-600 text-xs">Body</Label>
                          <Textarea
                            data-testid={`step-email-body-${index}`}
                            value={step.email_body}
                            onChange={e => updateStep(index, 'email_body', e.target.value)}
                            rows={4}
                            placeholder="Hi {agent_name},&#10;&#10;I wanted to follow up on the property at {property_address}..."
                            className={`${inputClass} resize-none`}
                          />
                          <p className="text-xs text-gray-400">
                            Variables: {'{property_address}'}, {'{agent_name}'}, {'{offer_amount}'}, {'{seller_name}'}, {'{buyer_name}'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Task fields */}
                    {step.action_type === 'create_task' && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <Label className="text-gray-600 text-xs">Task Title</Label>
                            <Input
                              data-testid={`step-task-title-${index}`}
                              value={step.task_title}
                              onChange={e => updateStep(index, 'task_title', e.target.value)}
                              placeholder="Call listing agent"
                              className={inputClass}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-gray-600 text-xs">Task Type</Label>
                            <Select value={step.task_type} onValueChange={v => updateStep(index, 'task_type', v)}>
                              <SelectTrigger data-testid={`step-task-type-${index}`} className={inputClass}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200">
                                {TASK_TYPES.map(t => (
                                  <SelectItem key={t} value={t} className="text-gray-900 capitalize">{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Notification fields */}
                    {step.action_type === 'notification' && (
                      <div className="space-y-1.5">
                        <Label className="text-gray-600 text-xs">Notification Message</Label>
                        <Input
                          data-testid={`step-notification-msg-${index}`}
                          value={step.notification_message}
                          onChange={e => updateStep(index, 'notification_message', e.target.value)}
                          placeholder="Reminder: Follow up on {property_address}"
                          className={inputClass}
                        />
                      </div>
                    )}

                    {/* Step Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => moveStep(index, -1)}
                          disabled={index === 0}
                          className="text-gray-400 hover:text-gray-600 h-8 px-2"
                        >
                          <ChevronUp className="h-4 w-4" /> Move Up
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => moveStep(index, 1)}
                          disabled={index === steps.length - 1}
                          className="text-gray-400 hover:text-gray-600 h-8 px-2"
                        >
                          <ChevronDown className="h-4 w-4" /> Move Down
                        </Button>
                      </div>
                      <Button
                        variant="ghost" size="sm"
                        data-testid={`remove-step-${index}`}
                        onClick={() => removeStep(index)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 px-2"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {steps.length > 0 && (
          <div className="flex justify-center">
            <Button
              onClick={addStep}
              variant="outline"
              size="sm"
              className="border-dashed border-gray-300 text-gray-500 hover:text-[#0073ea] hover:border-[#0073ea]/30"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Another Step
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
